import { ProseMirror } from "@progress/kendo-react-editor";

const { TextSelection } = ProseMirror;

/**
 * Searches the entire ProseMirror document for text matches.
 * @param {string} term - The search term to find
 * @param {Object} doc - The ProseMirror document node
 * @param {Object} options - Search options: { caseSensitive, wholeWord }
 * @returns {Array} Array of { from, to } position objects
 */
export const getAllTextMatches = (term, doc, options = { caseSensitive: false, wholeWord: false }) => {
  const found = [];
  if (!term) return found;

  let pattern = term;
  if (options.wholeWord) {
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    pattern = `\\b${escapedTerm}\\b`;
  }

  const flags = options.caseSensitive ? "g" : "gi";
  const regex = new RegExp(pattern, flags);

  doc.descendants((node, pos) => {
    if (node.isText) {
      let match;
      while ((match = regex.exec(node.text)) !== null) {
        found.push({ from: pos + match.index, to: pos + match.index + match[0].length });
      }
    }
  });

  return found;
};

/**
 * Highlights a specific match in the editor by setting the text selection.
 * Double requestAnimationFrame lets layout/DOM settle on first open (collab + Kendo mount).
 * @param {Object} view - ProseMirror EditorView
 * @param {Object} match - Match object with { from, to }
 * @param {Function} [onAfterFocus] - Optional callback to restore focus after highlight
 */
export const highlightMatch = (view, match, onAfterFocus) => {
  if (!view || !match) return;

  const activeElement = document.activeElement;

  const apply = () => {
      const doc = view.state.doc;
      const maxPos = doc.content.size;
      if (match.from < 0 || match.to > maxPos || match.from >= match.to) {
        return;
      }
      const selection = TextSelection.create(doc, match.from, match.to);
      const tr = view.state.tr.setSelection(selection).scrollIntoView();
      view.dispatch(tr);
      view.focus();
      if (onAfterFocus) {
        onAfterFocus(activeElement);
      }
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(apply);
  });
};
